import { BarChartProps } from '@mui/x-charts';
import {
  AxisValueFormatterContext,
  SeriesValueFormatter,
} from '@mui/x-charts/internals';
import produce from 'immer';
import { defaultTheme } from '../../../../../../../../theme/default-theme';
import {
  AxisConfigType,
  SingleOrDoubleAxisConfig,
  XAxisConfig,
  YAxisConfig,
} from '../common-chart-types';

interface LabelSize {
  width: number;
  height: number;
}

interface LabelSizes {
  first: LabelSize;
  last: LabelSize;
  widest: LabelSize;
  highest: LabelSize;
  widths: number[];
  heights: number[];
}

// Rotation applied to x axis tick labels unless they are wrapped
const X_TICK_LABEL_ROTATION_DEG = 30;
// MUI x-charts draws bottom axis tick labels at tickSize (6) + 3 below the axis line
const MUI_X_TICK_LABEL_OFFSET = 9;
// Space kept between neighbouring wrapped labels; also absorbs small differences
// between canvas measurement and the rendered text
const TICK_LABEL_GAP = 6;
const TICK_LABEL_BOTTOM_PADDING = 2;
// Approximates the line height MUI measures for each wrapped line
const TICK_LABEL_LINE_HEIGHT_RATIO = 1.3;
const MAX_WRAPPED_TICK_LABEL_LINES = 3;
// Font sizes tried, largest first, before long words are broken across lines
const TICK_FONT_SIZES = [12, 10, 9];
const DEFAULT_X_AXIS_MARGIN = 30;
const DEFAULT_LEFT_MARGIN = 40;
// Minimum width to show legend beside chart (below this, legend would take too much space)
const LEGEND_MIN_WIDTH_THRESHOLD = 500;
// Right margin to reserve for legend when shown
const LEGEND_RIGHT_MARGIN = 100;
const TICK_LABEL_FONT_FAMILY = `${defaultTheme.font.normal}, sans-serif`;

const measureLabelSizes = (
  labels: string[],
  fontSize: number,
  fontFamily = 'sans-serif'
): LabelSizes => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return {
      first: { width: 0, height: 0 },
      last: { width: 0, height: 0 },
      widest: { width: 0, height: 0 },
      highest: { width: 0, height: 0 },
      widths: [],
      heights: [],
    };
  }
  const widths: number[] = [];
  const heights: number[] = [];

  ctx.font = `${fontSize}px ${fontFamily}`;

  labels.forEach((label) => {
    const metrics = ctx.measureText(label);
    const width = metrics.width;
    const height = fontSize; // Approximate height based on fontSize

    widths.push(width);
    heights.push(height);
  });

  const widestIdx = widths.indexOf(Math.max(...widths));
  const highestIdx = heights.indexOf(Math.max(...heights));

  return {
    first: { width: widths[0], height: heights[0] },
    last: {
      width: widths[widths.length - 1],
      height: heights[heights.length - 1],
    },
    widest: { width: widths[widestIdx], height: heights[widestIdx] },
    highest: { width: widths[highestIdx], height: heights[highestIdx] },
    widths,
    heights,
  };
};

const calculateMaxVisibleTickLabelLength = (
  availableWidth: number,
  numTicks: number
) => {
  const sizePerTick = availableWidth / numTicks;
  if (sizePerTick > 100) {
    return 15;
  } else {
    return 12;
  }
};

const calculateTickFontSize = (availableWidth: number, numTicks: number) => {
  // if too many ticks for limited space, reduce font size
  const sizePerTick = availableWidth / numTicks;
  if (sizePerTick > 20) {
    return 12;
  }
  if (sizePerTick > 10) {
    return 10;
  }
  return 9;
};

export enum XTickLabelMode {
  // Default, same as before multi-line labels: rotated and shortened
  ROTATED = 'rotated',
  // Flat under the tick and wrapped onto as many lines as needed, never cut
  // off; turned on per chart from the chart tools
  WRAPPED = 'wrapped',
}

export interface XTickLabelLayout {
  mode: XTickLabelMode;
  fontSize: number;
  // Text drawn on the axis for each formatted label, with '\n' between wrapped lines
  tickTexts: Map<string, string>;
  maxLineCount: number;
  // Widest line of the first and last labels, which can overhang the plot on point scales
  edgeLabelWidths: { first: number; last: number };
}

export interface XTickLabelOptions {
  wrapLabels?: boolean;
  // Width left for the plot once the side margins are taken; see getPlotSideMargins
  plotWidth?: number;
}

export type ModifiedXAxisConfig<T> = XAxisConfig<T> & {
  isRotated: boolean;
  tickLabelLayout?: XTickLabelLayout;
};

type MeasureText = (text: string, fontSize: number) => number;

const NO_EDGE_LABEL_WIDTHS = { first: 0, last: 0 };

const createTextMeasurer = (): MeasureText | null => {
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) {
    return null;
  }
  return (text, fontSize) => {
    ctx.font = `${fontSize}px ${TICK_LABEL_FONT_FAMILY}`;
    return ctx.measureText(text).width;
  };
};

// A word, or a single CJK character since those can break anywhere, with the whitespace before it
const CJK_CHARS =
  '\\u3000-\\u30ff\\u3400-\\u9fff\\uf900-\\ufaff\\uff00-\\uffef';
const WRAP_TOKEN_REGEX = new RegExp(
  `(\\s*)([${CJK_CHARS}]|[^\\s${CJK_CHARS}]+)`,
  'g'
);

const WORD_BREAK_HYPHEN = '-';

// Splits a word wider than maxWidth into pieces that fit, hyphenating every piece but the last
const breakWord = (
  word: string,
  maxWidth: number,
  measure: (text: string) => number
) => {
  const pieces: string[] = [];
  let rest = word;
  while (measure(rest) > maxWidth) {
    // Always move at least one character on, even if it alone is too wide
    let end = 1;
    while (
      end < rest.length - 1 &&
      measure(`${rest.slice(0, end + 1)}${WORD_BREAK_HYPHEN}`) <= maxWidth
    ) {
      end++;
    }
    pieces.push(`${rest.slice(0, end)}${WORD_BREAK_HYPHEN}`);
    rest = rest.slice(end);
  }
  return [...pieces, rest];
};

// Greedy word wrap. A word wider than maxWidth makes it return null, unless
// breakWords is set, in which case the word is split across lines.
export const wrapTickLabel = (
  label: string,
  maxWidth: number,
  measure: (text: string) => number,
  breakWords = false
): string[] | null => {
  const lines: string[] = [];
  let line = '';
  for (const [, space, token] of label.matchAll(WRAP_TOKEN_REGEX)) {
    const candidate = line ? `${line}${space ? ' ' : ''}${token}` : token;
    if (measure(candidate) <= maxWidth) {
      line = candidate;
      continue;
    }
    if (line) {
      lines.push(line);
    }
    if (measure(token) <= maxWidth) {
      line = token;
      continue;
    }
    if (!breakWords) {
      return null;
    }
    const pieces = breakWord(token, maxWidth, measure);
    lines.push(...pieces.slice(0, -1));
    line = pieces[pieces.length - 1];
  }
  return [...lines, line];
};

const getWidestLineWidth = (
  tickTexts: Iterable<string>,
  measure: (text: string) => number
) =>
  Math.max(
    0,
    ...[...tickTexts].flatMap((text) => text.split('\n').map(measure))
  );

const getRotatedTickLabelLayout = (
  labels: string[],
  elementWidth: number,
  fontSize: number
): XTickLabelLayout => {
  const maxLength = calculateMaxVisibleTickLabelLength(
    elementWidth,
    labels.length
  );
  const tickTexts = new Map(
    labels.map((label) => [label, label.slice(0, maxLength)])
  );
  return {
    mode: XTickLabelMode.ROTATED,
    fontSize,
    tickTexts,
    maxLineCount: 1,
    edgeLabelWidths: NO_EDGE_LABEL_WIDTHS,
  };
};

// Picks how x axis tick labels are drawn. By default they are rotated and
// shortened to 12-15 characters. With wrapLabels the full label stays readable without hovering
// (charts get shared as screenshots): it wraps flat under the tick at the
// largest font that fits, and long words are broken rather than cut off.
export const getXTickLabelLayout = (
  labels: string[],
  elementWidth: number,
  {
    wrapLabels = false,
    plotWidth = elementWidth - DEFAULT_LEFT_MARGIN,
  }: XTickLabelOptions = {}
): XTickLabelLayout => {
  const baseFontSize = calculateTickFontSize(elementWidth, labels.length);
  const measure = wrapLabels ? createTextMeasurer() : null;
  if (!measure) {
    return getRotatedTickLabelLayout(labels, elementWidth, baseFontSize);
  }

  // Wrapped labels are always drawn (see tickLabelInterval below), so a label
  // may use its whole tick apart from a small gap to its neighbours
  const tickWidth = Math.max(0, plotWidth) / Math.max(labels.length, 1);
  const maxLineWidth = tickWidth - TICK_LABEL_GAP;
  const fontSizes = TICK_FONT_SIZES.filter((size) => size <= baseFontSize);
  const getWrappedLayout = (
    fontSize: number,
    wrapped: string[][]
  ): XTickLabelLayout => {
    const measureAtSize = (text: string) => measure(text, fontSize);
    const tickTexts = labels.map(
      (label, i) => [label, wrapped[i].join('\n')] as const
    );
    const edgeLabelWidth = (entry?: readonly [string, string]) =>
      entry ? getWidestLineWidth([entry[1]], measureAtSize) : 0;
    return {
      mode: XTickLabelMode.WRAPPED,
      fontSize,
      tickTexts: new Map(tickTexts),
      maxLineCount: Math.max(1, ...wrapped.map((lines) => lines.length)),
      edgeLabelWidths: {
        first: edgeLabelWidth(tickTexts[0]),
        last: edgeLabelWidth(tickTexts[tickTexts.length - 1]),
      },
    };
  };

  if (maxLineWidth <= 0) {
    // Not measured yet (first render), so there is nothing to wrap against
    return getWrappedLayout(
      baseFontSize,
      labels.map((label) => [label])
    );
  }

  // Largest font where every label wraps between words within the line limit
  for (const fontSize of fontSizes) {
    const measureAtSize = (text: string) => measure(text, fontSize);
    const wrapped = labels.map((label) =>
      wrapTickLabel(label, maxLineWidth, measureAtSize)
    );
    if (
      wrapped.every(
        (lines) =>
          lines !== null && lines.length <= MAX_WRAPPED_TICK_LABEL_LINES
      )
    ) {
      return getWrappedLayout(fontSize, wrapped as string[][]);
    }
  }

  // Otherwise use the smallest font and break long words, with as many lines
  // as needed: labels are never cut off
  const smallestFontSize = fontSizes[fontSizes.length - 1] ?? baseFontSize;
  return getWrappedLayout(
    smallestFontSize,
    labels.map(
      (label) =>
        wrapTickLabel(
          label,
          maxLineWidth,
          (text) => measure(text, smallestFontSize),
          true
        ) ?? [label]
    )
  );
};

const getModifiedXAxisConfig = (
  xAxisConfig: XAxisConfig<string>,
  elementWidth: number,
  dataset: BarChartProps['dataset'],
  isTopAxis: boolean,
  options: XTickLabelOptions
): ModifiedXAxisConfig<string> => {
  const dataKey = xAxisConfig.dataKey;
  if (!dataKey) {
    throw new Error('x axis config dataKey not found');
  }
  const formatTick = (d: any): string =>
    String(
      xAxisConfig.valueFormatter
        ? xAxisConfig.valueFormatter(d, { location: 'tick' })
        : d
    );
  const labels = dataset?.map((d) => formatTick(d[dataKey])) ?? [];
  const tickLabelLayout = getXTickLabelLayout(labels, elementWidth, options);
  const valueFormatter = (d: any, context: AxisValueFormatterContext) => {
    const formattedTick = formatTick(d);
    if (context.location !== 'tick') {
      return formattedTick;
    }
    return tickLabelLayout.tickTexts.get(formattedTick) ?? formattedTick;
  };
  const { mode, fontSize } = tickLabelLayout;
  // An axis without labels has nothing to rotate (and needs no extra margin)
  const isRotated = mode === XTickLabelMode.ROTATED && labels.length > 0;
  const angle = isRotated ? -X_TICK_LABEL_ROTATION_DEG : 0;
  const modifiedXAxisConfig = produce(xAxisConfig, (draft) => {
    draft.tickLabelStyle = {
      ...draft.tickLabelStyle,
      angle: isTopAxis ? -angle : angle,
      textAnchor: isRotated ? 'end' : 'middle',
      fontSize,
    };
    draft.valueFormatter = valueFormatter;
    // MUI drops labels it thinks would overlap. Wrapped labels are sized to
    // fit, and a missing name can't be recovered from a screenshot, so always
    // draw them. Rotated labels on crowded axes still rely on skipping.
    if (!isRotated) {
      draft.tickLabelInterval = () => true;
    }
  });
  return {
    ...modifiedXAxisConfig,
    isRotated,
    tickLabelLayout,
  };
};

// modified to wrap, rotate or shorten tick labels so they fit
export const getModifiedXAxisConfigs = (
  {
    isHorizontal,
    xAxisConfigs,
  }: {
    isHorizontal: boolean;
    xAxisConfigs: SingleOrDoubleAxisConfig<XAxisConfig<any>>;
  },
  dataset: BarChartProps['dataset'],
  elementWidth: number,
  options: XTickLabelOptions = {}
): SingleOrDoubleAxisConfig<ModifiedXAxisConfig<any>> => {
  // only wrapping labels for non-horizontal charts;
  // horizontal charts keep shortened labels
  const axisOptions = isHorizontal
    ? { ...options, wrapLabels: false }
    : options;
  const modifyAxis = (axisConfig: XAxisConfig<any>, isTopAxis: boolean) => {
    const modified = getModifiedXAxisConfig(
      axisConfig,
      elementWidth,
      dataset,
      isTopAxis,
      axisOptions
    );
    return isHorizontal ? { ...modified, isRotated: false } : modified;
  };
  if (xAxisConfigs.type === AxisConfigType.SINGLE) {
    return {
      type: AxisConfigType.SINGLE,
      firstAxisConfig: modifyAxis(xAxisConfigs.firstAxisConfig, false),
    };
  }
  return {
    type: AxisConfigType.DOUBLE,
    firstAxisConfig: modifyAxis(xAxisConfigs.firstAxisConfig, false),
    secondAxisConfig: modifyAxis(xAxisConfigs.secondAxisConfig, true),
  };
};

const calcMarginBufferBasedOnYAxisValues = (yAxisValues: string[]) => {
  const BASE_VALUE = 3;
  // base character width is based on trial and error
  const BASE_CHARACTER_WIDTH = 8;
  const maxValueLength = Math.max(...yAxisValues.map((v) => v.length));
  return Math.max(0, maxValueLength - BASE_VALUE) * BASE_CHARACTER_WIDTH;
};

// add margin based on y axis values
const getYAxisMarginBuffer = (
  series: {
    dataKey: string;
    valueFormatter?: SeriesValueFormatter<number | null>;
  }[], // must have dataKey so we can get the values
  datasetArg: BarChartProps['dataset'] = []
): number => {
  const yAxisValues = series.flatMap((s) =>
    datasetArg.map((d, i) => {
      const num = Math.floor(Number(d[s.dataKey] ?? 0));
      // This is some brittle logic, but basically if the number
      // the axis of mui internally has some logic which formats
      // values like 5186.1688649869869 to 5186 or 5200, so to estimate
      // the length of the number on the tick, we'll need the floored value
      // I suppose for smaller numbers, mui doesn't do this rounding
      // so I have the > 10 check
      const modifiedNum = num > 10 ? Math.floor(num) : num;
      return (
        s.valueFormatter?.(modifiedNum, {
          dataIndex: i,
        }) ?? String(modifiedNum)
      );
    })
  );
  return calcMarginBufferBasedOnYAxisValues(yAxisValues);
};

// Vertical space beyond the axis line taken by the widest rotated tick label
const getRequiredMarginForRotatedTicks = (
  labels: string[],
  fontSize: number
) => {
  if (!labels.length) {
    return 0;
  }
  const { widest } = measureLabelSizes(
    labels,
    fontSize,
    TICK_LABEL_FONT_FAMILY
  );
  const widestWidth = widest.width || 0;
  const radAngle = (X_TICK_LABEL_ROTATION_DEG * Math.PI) / 180;
  return Math.ceil(
    MUI_X_TICK_LABEL_OFFSET +
      widestWidth * Math.sin(radAngle) +
      fontSize * Math.cos(radAngle) +
      TICK_LABEL_BOTTOM_PADDING
  );
};

// Vertical space beyond the axis line taken by wrapped tick labels
const getRequiredMarginForWrappedTicks = ({
  fontSize,
  maxLineCount,
}: XTickLabelLayout) =>
  Math.ceil(
    MUI_X_TICK_LABEL_OFFSET +
      maxLineCount * fontSize * TICK_LABEL_LINE_HEIGHT_RATIO +
      TICK_LABEL_BOTTOM_PADDING
  );

// Note: In @mui/x-charts v8, legends are HTML elements (not SVG) and can be positioned
// outside the chart using <ChartsWrapper legendPosition={{...}}> without manual margins.

// Note: This only works correctly for non horizontal charts
// Left and right margins before any x axis label adjustments; they don't depend
// on the x axis, so charts can compute them first to size the x tick labels
export const getPlotSideMargins = (
  yAxisConfigs: SingleOrDoubleAxisConfig<YAxisConfig<any>> | undefined,
  series: {
    dataKey: string;
    valueFormatter?: SeriesValueFormatter<number | null>;
  }[],
  dataset: BarChartProps['dataset'] = [],
  elementWidth: number,
  showLegend = false
) => {
  let left = DEFAULT_LEFT_MARGIN;
  let right = 0;
  // Add right margin for legend only if chart is wide enough
  if (showLegend && elementWidth >= LEGEND_MIN_WIDTH_THRESHOLD) {
    right += LEGEND_RIGHT_MARGIN;
  }
  if (yAxisConfigs?.type === AxisConfigType.DOUBLE) {
    // if right axis, add right margin
    right += 20;
  }
  left += getYAxisMarginBuffer(series, dataset);
  return { left, right };
};

export const getMarginsForNonHorizontalCharts = (
  xAxisConfigs: SingleOrDoubleAxisConfig<ModifiedXAxisConfig<string>>,
  yAxisConfigs: SingleOrDoubleAxisConfig<YAxisConfig<number>> | undefined,
  // Right now, the behaviour for right y-axis is not correct. We just add
  // a fixed margin for it, but don't calculate it based on the data for right axis values.
  // We currently assume that all the data values are showing on the left y-axis,
  // as we don't consider how different series could be tied to different y-axes.
  series: {
    dataKey: string;
    valueFormatter?: SeriesValueFormatter<number | null>;
  }[], // must have dataKey so we can get the values
  dataset: BarChartProps['dataset'] = [],
  elementWidth: number,
  showLegend: boolean = false
) => {
  const sideMargins = getPlotSideMargins(
    yAxisConfigs,
    series,
    dataset,
    elementWidth,
    showLegend
  );
  let leftMargin = sideMargins.left;
  let rightMargin = sideMargins.right;
  let topMargin = 20;
  let bottomMargin = DEFAULT_X_AXIS_MARGIN;

  if (xAxisConfigs.type === AxisConfigType.DOUBLE) {
    // if top axis, add top margin
    topMargin += 30;
  }

  // add margin for rotated x axis ticks
  // how much margin depends on the width of the longest label
  const fontSize = calculateTickFontSize(elementWidth, dataset.length);
  // roughly basing this on bottom x axis label
  const labels = dataset.map((d) => {
    const xAxisConfig = xAxisConfigs.firstAxisConfig;
    const { dataKey, valueFormatter } = xAxisConfig;
    if (!dataKey) {
      return '';
    }
    const value = String(d[dataKey]);
    return valueFormatter ? valueFormatter(value, { location: 'tick' }) : value;
  });

  const labelSizes = measureLabelSizes(labels, fontSize);
  const maxXAxisLabelSize = labelSizes.widest.width;
  let rotationXBuffer = 0;
  let rotationYBuffer = 0;

  // This is all based on trial and error
  if (maxXAxisLabelSize > 80) {
    rotationXBuffer = 20;
    rotationYBuffer = 40;
  } else if (maxXAxisLabelSize > 60) {
    rotationXBuffer = 20;
    rotationYBuffer = 30;
  } else if (maxXAxisLabelSize > 40) {
    rotationXBuffer = 10;
    rotationYBuffer = 20;
  } else {
    rotationXBuffer = 0;
    rotationYBuffer = 10;
  }

  const firstAxis = xAxisConfigs.firstAxisConfig;
  if (firstAxis.isRotated) {
    bottomMargin += rotationYBuffer;
    leftMargin += rotationXBuffer;
    // The buffer above is an estimate; never let it be smaller than what the
    // rotated labels actually need, otherwise their start is clipped
    bottomMargin = Math.max(
      bottomMargin,
      getRequiredMarginForRotatedTicks(labels, fontSize)
    );
  } else if (firstAxis.tickLabelLayout?.mode === XTickLabelMode.WRAPPED) {
    // Wrapped labels are centered on their tick, so only the height grows
    bottomMargin = Math.max(
      bottomMargin,
      getRequiredMarginForWrappedTicks(firstAxis.tickLabelLayout)
    );
    // Point scales put the first and last ticks on the plot edges, so half of
    // those labels hangs over into the side margins
    if (firstAxis.scaleType === 'point') {
      const { first, last } = firstAxis.tickLabelLayout.edgeLabelWidths;
      leftMargin = Math.max(leftMargin, Math.ceil(first / 2));
      rightMargin = Math.max(rightMargin, Math.ceil(last / 2));
    }
  }
  if (xAxisConfigs.type === AxisConfigType.DOUBLE) {
    const secondAxis = xAxisConfigs.secondAxisConfig;
    if (secondAxis.isRotated) {
      topMargin += rotationYBuffer;
    } else if (secondAxis.tickLabelLayout?.mode === XTickLabelMode.WRAPPED) {
      // The double axis top margin above already fits one line of labels
      topMargin += Math.max(
        0,
        getRequiredMarginForWrappedTicks(secondAxis.tickLabelLayout) -
          DEFAULT_X_AXIS_MARGIN
      );
    }
  }

  return {
    right: rightMargin,
    left: leftMargin,
    top: topMargin,
    bottom: bottomMargin,
  };
};
